import { jwtDecode } from "jwt-decode";

import { getBaseUrl } from "./base-url";
import { showSample } from "./demo-preference";
import { DashboardDto } from "./generated/api-client";
import { navDashboards } from "./nav-dashboards";
import { peekPendingConnection } from "./pending-connection";

type Collection = { name?: string; fields?: Array<{ name?: string }> };

type GeneratedDashboard = {
  id?: string;
  name?: string;
  dataTables?: Array<{ collection?: string }>;
  charts?: Array<{ collections?: string[] }>;
  forms?: Array<{ fields?: Array<{ collection?: string }> }>;
};

type Attempt = {
  status?: string;
  errorMessage?: string | null;
  databaseCollections?: Collection[];
  uiGenerationAttempts?: Array<{
    panels?: Array<{
      dashboards?: GeneratedDashboard[];
    }>;
  }>;
};

type DemoDatasource = {
  id?: string;
  isDemo?: boolean;
  analyzeAttempts?: Attempt[];
};

/** Attempts are stored oldest-first; the last entry is the one just run. */
export function latestAttempt<T>(attempts: T[] | undefined): T | undefined {
  if (!attempts?.length) return undefined;
  return attempts[attempts.length - 1];
}

export function latestSuccessAttempt<T extends { status?: string }>(
  attempts: T[] | undefined,
): T | undefined {
  return [...(attempts ?? [])]
    .reverse()
    .find((a) => a.status === "SUCCESS");
}

export function latestStoredAttempt<T extends { status?: string }>(
  attempts: T[] | undefined,
): T | undefined {
  return [...(attempts ?? [])]
    .reverse()
    .find((a) => a.status === "SUCCESS" || a.status === "FAILED");
}

export function latestFailedAttempt<T extends { status?: string }>(
  attempts: T[] | undefined,
): T | undefined {
  const latest = latestStoredAttempt(attempts);
  return latest?.status === "FAILED" ? latest : undefined;
}

/** Tables everyone recognises, so a demo opens on something readable. */
const PREFERRED_NAMES = [
  "projects",
  "users",
  "customers",
  "orders",
  "products",
  "tasks",
  "invoices",
  "accounts",
];

const isIdish = (name: string) =>
  /^(id|_id|uuid)$/i.test(name) || /(_id|Id)$/.test(name);

/**
 * `databaseCollections[0]` is whatever `information_schema` happened to return
 * first, which on the demo schema is `task_dependencies`: two foreign keys and
 * a single row. Rank the tables instead so the first thing a visitor sees has
 * columns worth looking at.
 */
export function rankedCollections<T extends Collection>(
  collections: T[] | undefined,
): T[] {
  const named = (collections ?? []).filter((c) => !!c.name);

  const score = (collection: Collection): number => {
    const fields = collection.fields ?? [];
    const preferred = PREFERRED_NAMES.indexOf(
      (collection.name ?? "").toLowerCase(),
    );
    const contentFields = fields.filter(
      (f) => !!f.name && !isIdish(f.name),
    ).length;
    // A join table is all keys and nothing else; it reads as an empty grid.
    const junction = contentFields === 0;
    return (
      // A recognised name wins outright; field counts only break ties.
      (preferred >= 0 ? 10000 - preferred * 100 : 0) +
      (junction ? -500 : 0) +
      contentFields * 10 +
      fields.length
    );
  };

  return [...named].sort((a, b) => score(b) - score(a));
}

export function pickHighlightCollection(
  collections: Collection[] | undefined,
): string | undefined {
  return rankedCollections(collections)[0]?.name;
}

export function latestSuccessCollection(
  attempts: Attempt[] | undefined,
): string | undefined {
  return pickHighlightCollection(
    latestSuccessAttempt(attempts)?.databaseCollections,
  );
}

const namedCollectionCount = (attempt: Attempt): number =>
  (attempt.databaseCollections ?? []).filter((collection) => !!collection.name)
    .length;

const generatedDashboardCount = (attempt: Attempt): number =>
  (attempt.uiGenerationAttempts?.[0]?.panels ?? []).reduce(
    (total, panel) => total + (panel.dashboards?.length ?? 0),
    0,
  );

/** Pick one stable demo rather than whichever demo the API returned first. */
export function pickDemoDatasource<T extends DemoDatasource>(
  datasources: T[] | undefined,
): T | undefined {
  const successfulDemos = (datasources ?? [])
    .filter((datasource) => datasource.isDemo)
    .map((datasource) => ({
      datasource,
      success: latestSuccessAttempt(datasource.analyzeAttempts),
    }))
    .filter(
      (candidate): candidate is { datasource: T; success: Attempt } =>
        !!candidate.success,
    );

  const demosWithGeneratedUi = successfulDemos.filter(
    ({ success }) => generatedDashboardCount(success) > 0,
  );
  const candidates = demosWithGeneratedUi.length
    ? demosWithGeneratedUi
    : successfulDemos;

  return candidates.reduce<(typeof candidates)[number] | undefined>(
    (best, candidate) =>
      !best ||
      namedCollectionCount(candidate.success) >
        namedCollectionCount(best.success)
        ? candidate
        : best,
    undefined,
  )?.datasource;
}

/**
 * Where a guest (or anyone just authed) should land on the demo.
 *
 * Same rule as DatasourceCard.demoEntry: if navDashboards keeps a page, that
 * is a real overview and first-run goes there. Otherwise ranked collection
 * fallback (not information_schema[0], which is task_dependencies).
 */
export function demoLandPath(demo: DemoDatasource | undefined): string {
  if (!demo?.id) {
    return "/data-sources";
  }

  const success = latestSuccessAttempt(demo.analyzeAttempts);
  const collectionNames = (success?.databaseCollections ?? [])
    .map((collection) => collection.name)
    .filter((name): name is string => !!name);
  const generated = (success?.uiGenerationAttempts?.[0]?.panels ?? []).flatMap(
    (panel) => panel.dashboards ?? [],
  );
  const overview = navDashboards(
    generated as DashboardDto[],
    collectionNames,
  )[0];
  if (overview?.id) {
    return `/datasources/${demo.id}/dashboards/${overview.id}`;
  }

  const collection = latestSuccessCollection(demo.analyzeAttempts);
  if (collection) {
    return `/datasources/${demo.id}/collections/${encodeURIComponent(collection)}`;
  }
  return "/data-sources";
}

/**
 * Where a signed-in user (or a guest who did not ask for the sample) lands.
 * Nobody is sent to the shared sample by default — that is opt-in via
 * "Try a sample" or "Open the demo as a guest".
 */
export async function pathAfterAuth(_token: string): Promise<string> {
  // A pending connection (website hand-off) belongs on the datasource list,
  // where useDataSourceManagement opens the prefilled connect dialog.
  if (peekPendingConnection()) {
    return "/data-sources";
  }
  return "/data-sources";
}

/**
 * Guest chose the sample explicitly. Turn the preference on and land on the
 * demo overview (or ranked collection) when one exists.
 */
export async function pathAfterGuestDemo(token: string): Promise<string> {
  // Key the sample flag by the guest account so a later signed-in user does
  // not inherit this opt-in.
  let accountId: string | null = null;
  try {
    const payload = jwtDecode<{
      guest?: { id?: string };
      user?: { id?: string };
    }>(token);
    accountId =
      (typeof payload?.guest?.id === "string" && payload.guest.id) ||
      (typeof payload?.user?.id === "string" && payload.user.id) ||
      null;
  } catch {
    accountId = null;
  }
  if (!accountId && typeof localStorage !== "undefined") {
    accountId = localStorage.getItem("guest");
  }
  showSample(accountId);
  const res = await fetch(`${getBaseUrl()}/datasources`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    return "/data-sources";
  }
  const datasources = (await res.json()) as DemoDatasource[];
  return demoLandPath(pickDemoDatasource(datasources));
}

/**
 * Open path for an existing datasource: first dashboard when one exists,
 * otherwise the data-sources list with that card highlighted.
 */
export function pathForExistingDatasource(
  datasource: DemoDatasource | undefined | null,
): string {
  if (!datasource?.id) return "/data-sources";
  const land = demoLandPath(datasource);
  if (land !== "/data-sources") return land;
  return `/data-sources?highlight=${encodeURIComponent(datasource.id)}`;
}
